import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toLocalDateString, getInitials, rsdFormat, stripDiacritics } from '../utils/helper';
import { getCurrentUser } from '../services/auth';
import { isOfficeUser, isUserAllowed } from '../utils/roleHelpers';
import RevenueAreaChart from '../components/charts/revenueAreaChart';
import SetTempEditOrderModal from '../components/modals/orderEditTemp';
import SetOrderModal from '../components/modals/orderSet';
import SetClientModal from './clients/clientsSet';
import IntakeLinkDrawer from '../components/modals/intakeLinkDrawer';
import { getDashboardOverview } from '../services/overview';
import { getLowStockCount } from '../services/materials';
import useCompanyFeatures from '../hooks/useCompanyFeatures';
import JobsSelect from '../components/selects/jobsSelect';
import { updateJob } from '../services/jobs';
import { invalidateJobStats } from '../utils/invalidateStats';
import InvoiceSharePanel from '../components/invoiceSharePanel';
import { getStatusMeta, getStatusIndex, getStatusOptions } from '../constants/jobStatus';
import { MISC_LABELS } from '../constants/labels';

/* ---- Phosphor icons (react-icons/pi) — clean, balanced, professional ---- */
import {
    PiPulse as IconPulse,
    PiCheck as IconCheck,
    PiCreditCard as IconCard,
    PiClock as IconClock,
    PiWallet as IconWallet,
    PiCalendarDots as IconCalendar,
    PiArrowUpRight as IconArrowUR,
    PiArrowDownRight as IconArrowDR,
    PiWarning as IconAlert,
    PiMapPin as IconPin,
    PiChartBar as IconReports,
    PiPlus as IconPlus,
    PiUser as IconUser,
    PiTruck as IconTruck,
    PiPackage as IconPickup,
    PiSparkle as IconSparkle,
    PiFileText as IconNote,
    PiCaretRight as IconChevron,
    PiDotsThree as IconMore,
    PiPhone as IconPhone,
    PiEnvelopeSimple as IconMail,
    PiScan as IconCamera,
} from 'react-icons/pi';

/* count formatter (locale grouping, no decimals) */
const fmt = (n) => (n === undefined || n === null || isNaN(n)) ? '0' : Number(n).toLocaleString();

/* ISO week number */
const isoWeek = (d) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = (date.getUTCDay() + 6) % 7;
    date.setUTCDate(date.getUTCDate() - dayNum + 3);
    const firstThursday = date.getTime();
    date.setUTCMonth(0, 1);
    if (date.getUTCDay() !== 4) date.setUTCMonth(0, 1 + ((4 - date.getUTCDay()) + 7) % 7);
    return 1 + Math.ceil((firstThursday - date.getTime()) / 604800000);
};

const greetingKey = (h) => h < 12 ? 'home.goodMorning' : h < 18 ? 'home.goodAfternoon' : 'home.goodEvening';

/* Week-over-week trend → { cls, label, sub, arrow } for the KPI delta chips.
   tr.isCount = true  → label is an absolute count ("+4"), no % sign
   tr.isCount = false → label is a percentage ("11,0%")
   Neutral when no activity this week so a quiet week never reads as "-100%". */
const trendInfo = (tr) => {
    if (!tr || tr.delta === undefined || tr.delta === null) return { cls: 'flat', labelKey: null, label: '—' };
    if (!tr.activeThisWeek) return { cls: 'flat', labelKey: 'home.noActivity' };
    if (tr.delta === 0) return { cls: 'flat', labelKey: 'home.noChange' };
    const up = tr.delta > 0;
    const abs = Math.abs(tr.delta);
    const chip = tr.isCount
        ? `${abs}`
        : `${abs.toFixed(1).replace('.', ',')}%`;
    return { cls: up ? 'up' : 'down', label: chip, subKey: 'home.vsLastWeek', arrow: up };
};

/* pickupTime can be a date string or a sentinel like "To Be Delivered" */
const hm = (val) => {
    if (!val) return '—';
    const t = new Date(val);
    return isNaN(t.getTime()) ? '—' : toLocalDateString(t, 'HH:mm');
};

/* Job lifecycle status → redesign stage accent + badge label.
   Labels/colors come from the single source of truth in
   `src/constants/jobStatus.js` — `stage()` transparently handles both new
   canonical statuses and old pre-redesign ones still on existing records. */
const TONE_TO_CLS = { grey: 's-grey', amber: 's-amber', green: 's-green', cancelled: 's-cancelled' };
const TONE_TO_BADGE = { grey: 'b-grey', amber: 'b-amber', green: 'b-green', cancelled: 'b-cancelled' };
const stage = (status) => {
    const meta = getStatusMeta(status);
    return { cls: TONE_TO_CLS[meta.tone], label: meta.label, badge: TONE_TO_BADGE[meta.tone] };
};

/* area in m² (0 → hidden) */
const m2 = (area) => { const a = Number(area) || 0; return a > 0 ? `${a % 1 === 0 ? a : a.toFixed(1)} m²` : null; };

const queryOptions = { keepPreviousData: true, staleTime: 1000 * 60, refetchOnMount: true };

const HomePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { materialsEnabled } = useCompanyFeatures();
    const [editJobModal, setEditJobModal] = useState(false);
    const [newJobModal, setNewJobModal] = useState(false);
    const [newClientModal, setNewClientModal] = useState(false);
    // Customer request link drawer (item 3.2) — office roles only.
    const [intakeLinkDrawer, setIntakeLinkDrawer] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState('');
    const [quickMenuId, setQuickMenuId] = useState('');
    const [quickPanel, setQuickPanel] = useState(null);
    const [quickStatus, setQuickStatus] = useState('');
    const [savingStatus, setSavingStatus] = useState(false);
    const [statusError, setStatusError] = useState('');
    const scanFileRef = useRef(null);

    // "Scan" quick action: pick an image here, then hand it straight to the
    // Ask Soroby panel (opens itself + runs the same scan flow as attaching a
    // file inside the chat) via a custom window event — Ask Soroby is mounted
    // globally in the dashboard template, not on this page.
    const onPickScanFile = (e) => {
        const file = e.target.files?.[0];
        if (file) window.dispatchEvent(new CustomEvent('soroby:scan-image', { detail: { file } }));
        e.target.value = '';
    };

    const now = new Date();
    const user = getCurrentUser();
    const firstName = (user?.fullName || '').trim().split(' ')[0] || t('home.greetingFallbackName');
    const allowed = isUserAllowed();
    const officeUser = isOfficeUser();

    const { data: dashboardData, isFetching: loadingDashboard } = useQuery(['overview-dashboard'], getDashboardOverview, queryOptions);
    const dashboard = dashboardData?.message ?? dashboardData ?? {};
    const { data: lowStockData } = useQuery(['low-stock-count'], getLowStockCount, {
        ...queryOptions,
        enabled: materialsEnabled,
        retry: false,
        onError: () => {},
    });
    const lowStockCount = Number(lowStockData?.message?.count) || 0;

    const companyName = dashboard.companyName || t('settingsPage.company');
    const todayJobs = Array.isArray(dashboard.todayJobs) ? dashboard.todayJobs : [];
    const upcomingPickups = Array.isArray(dashboard.upcomingPickups) ? dashboard.upcomingPickups : [];
    const upcomingDeliveries = Array.isArray(dashboard.upcomingDeliveries) ? dashboard.upcomingDeliveries : [];
    const finishedToday = Array.isArray(dashboard.finishedToday?.jobs) ? dashboard.finishedToday.jobs : [];
    const finishedYesterday = Array.from({ length: Number(dashboard.finishedToday?.yesterdayTotal) || 0 });
    const loadingTotals = loadingDashboard;
    const loadingToday = loadingDashboard;
    const loadingPickups = loadingDashboard;
    const loadingDeliveries = loadingDashboard;
    const totals = {
        paid: dashboard.paymentOverview?.paidCount,
        unpaid: dashboard.paymentOverview?.unpaidCount,
        paidAmount: dashboard.paymentOverview?.paidAmount,
        unpaidAmount: dashboard.paymentOverview?.unpaidAmount,
    };

    /* Legacy calls were replaced by the tenant-scoped /overview/dashboard response. */
    /*
    // No date params → returns ALL ASSIGNED/SCHEDULED jobs so jobs without a
    // pickupTime (created without schedule) still surface here, not just
    // ones explicitly scheduled for today.
    const { data: todayJobsData, isFetching: loadingToday } = useQuery(
        ['overview-today-jobs', todayStart, todayEnd], () => getUpcomingJobs(todayStart, todayEnd), queryOptions);
    useEffect(() => { onQuerySuccess(todayJobsData, (r) => setTodayJobs(Array.isArray(r) ? r : [])); }, [todayJobsData]);

    const [upcomingPickups, setUpcomingPickups] = useState([]);
    const { data: upcomingPickupsData, isFetching: loadingPickups } = useQuery(
        ['overview-upcoming-pickups'], () => getUpcomingJobs(), queryOptions);
    useEffect(() => {
        onQuerySuccess(upcomingPickupsData, (result) => {
            const currentTime = Date.now();
            const pickups = (Array.isArray(result) ? result : [])
                .filter((job) => {
                    const pickupTime = new Date(job.pickupTime).getTime();
                    return !isNaN(pickupTime) && pickupTime >= currentTime && getStatusIndex(job.status) <= getStatusIndex(JOB_STATUS.ARRIVED);
                })
                .sort((a, b) => new Date(a.pickupTime) - new Date(b.pickupTime));
            setUpcomingPickups(pickups);
        });
    }, [upcomingPickupsData]);

    const [upcomingDeliveries, setUpcomingDeliveries] = useState([]);
    const { data: upcomingDeliveriesData, isFetching: loadingDeliveries } = useQuery(
        ['overview-upcoming-deliveries'], () => getUpcomingDeliveries(), queryOptions);
    useEffect(() => {
        onQuerySuccess(upcomingDeliveriesData, (result) => {
            const currentTime = Date.now();
            const deliveries = (Array.isArray(result) ? result : [])
                .filter((job) => {
                    const deliveryTime = new Date(job.deliveryTime).getTime();
                    return !isNaN(deliveryTime) && deliveryTime >= currentTime;
                })
                .sort((a, b) => new Date(a.deliveryTime) - new Date(b.deliveryTime));
            setUpcomingDeliveries(deliveries);
        });
    }, [upcomingDeliveriesData]);

    const [finishedToday, setFinishedToday] = useState([]);
    const { data: finishedTodayData } = useQuery(
        ['overview-finished-today', todayStart, todayEnd], () => getFinishedJobs(todayStart, todayEnd), queryOptions);
    useEffect(() => { onQuerySuccess(finishedTodayData, (r) => setFinishedToday(Array.isArray(r) ? r : [])); }, [finishedTodayData]);

    const [finishedYesterday, setFinishedYesterday] = useState([]);
    const { data: finishedYesterdayData } = useQuery(
        ['overview-finished-yesterday', yesterdayStart, yesterdayEnd], () => getFinishedJobs(yesterdayStart, yesterdayEnd), queryOptions);
    useEffect(() => { onQuerySuccess(finishedYesterdayData, (r) => setFinishedYesterday(Array.isArray(r) ? r : [])); }, [finishedYesterdayData]);
    */

    // --- derived from /overview/totals ---
    // All-time (donut + invoice counts)
    const paidCount = Number(totals?.paid) || 0;
    const unpaidCount = Number(totals?.unpaid) || 0;
    const paidAmt = Number(totals?.paidAmount) || 0;
    const unpaidAmt = Number(totals?.unpaidAmount) || 0;
    const billedAmt = paidAmt + unpaidAmt;
    const paidPct = billedAmt > 0 ? Math.round((paidAmt / billedAmt) * 100) : 0;
    const unpaidPct = billedAmt > 0 ? 100 - paidPct : 0;
    // This-week / last-week (KPI tiles)
    const twTotal = Number(totals?.thisWeek?.total) || 0;
    const lwTotal = Number(totals?.lastWeek?.total) || 0;
    const twCompleted = Number(totals?.thisWeek?.completed) || 0;
    const twPaid = Number(totals?.thisWeek?.paid) || 0;
    const weekCompletedPct = twTotal > 0 ? Math.round((twCompleted / twTotal) * 100) : 0;
    const pickupCount = Number(dashboard.dailyBrief?.pickupsToday) || 0;
    const deliveryCount = Number(dashboard.dailyBrief?.deliveriesToday) || 0;
    const unassignedCount = Number(dashboard.needsAttention?.unassigned) || 0;
    const overduePickupCount = Number(dashboard.needsAttention?.overduePickup) || 0;
    const waiting24hCount = Number(dashboard.needsAttention?.waiting24h) || 0;
    const withoutNotesCount = Number(dashboard.needsAttention?.withoutNotes) || 0;
    const overdueInvoicesCount = Number(dashboard.needsAttention?.overdueInvoices) || 0;
    const needsRescheduleCount = Number(dashboard.needsAttention?.needsReschedule) || 0;

    // KPI cards that jump down to a panel already on this same page instead of
    // navigating away. Both the mobile and desktop layouts render their own copy
    // of that panel at once (CSS decides which is visible), so scroll whichever
    // one is currently on screen.
    const scrollToSection = (selector) => {
        const sections = document.querySelectorAll(selector);
        for (const section of sections) {
            if (section.offsetParent !== null) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                break;
            }
        }
    };
    const scrollToNeedsAttention = () => scrollToSection('.needs-attention');
    const scrollToTodaysJobs = () => scrollToSection('.today-schedule');
    const scrollToPickups = () => scrollToSection('.upcoming-pickups:not(.upcoming-deliveries)');
    const scrollToDeliveries = () => scrollToSection('.upcoming-deliveries');

    const resolvedJob = (job) => job || {};
    const openJob = (job) => {
        if (!job?.id) return;
        setSelectedJobId(job.id);
        setEditJobModal(true);
    };

    const openQuickPanel = (type, job) => {
        const current = resolvedJob(job);
        if (type === 'status') {
            setQuickStatus(current.status || 'SCHEDULED');
            setStatusError('');
        }
        setQuickPanel({ type, job: current });
        setQuickMenuId('');
    };

    const saveQuickStatus = async (job) => {
        if (!quickStatus || quickStatus === job.status) {
            setQuickPanel(null);
            return;
        }
        setSavingStatus(true);
        setStatusError('');
        try {
            const response = await updateJob({ id: job.id, status: quickStatus });
            if (response?.code !== 200) throw new Error(response?.message || t('home.statusCouldNotBeChanged'));
            invalidateJobStats(queryClient);
            setQuickPanel(null);
        } catch (error) {
            setStatusError(error?.message || t('home.statusCouldNotBeChanged'));
        } finally {
            setSavingStatus(false);
        }
    };


    /* ---- shared bits ---- */
    const Donut = ({ paidColor, unpaidColor = 'var(--warn)' }) => (
        <svg width="116" height="116" viewBox="0 0 42 42" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="21" cy="21" r="15.915" fill="none" stroke="var(--surface)" strokeWidth="6" />
            <circle cx="21" cy="21" r="15.915" fill="none" stroke={paidColor} strokeWidth="6"
                strokeDasharray={`${paidPct} ${unpaidPct}`} strokeDashoffset="0" strokeLinecap={paidPct > 0 && paidPct < 100 ? 'round' : 'butt'} />
            <circle cx="21" cy="21" r="15.915" fill="none" stroke={unpaidColor} strokeWidth="6"
                strokeDasharray={`${unpaidPct} ${paidPct}`} strokeDashoffset={`-${paidPct}`} strokeLinecap={unpaidPct > 0 && unpaidPct < 100 ? 'round' : 'butt'} />
        </svg>
    );

    const todaysJobRows = (
        loadingToday
            ? <div className="sbr-empty"><span>{t('home.loadingTodaysSchedule')}</span></div>
            : todayJobs.length === 0
                ? <div className="sbr-empty"><IconCalendar className="ic" /><span>{t('home.nothingScheduledForToday')}</span></div>
                : null
    );

    const QuickActions = ({ job }) => {
        const current = resolvedJob(job);
        const menuOpen = quickMenuId === job.id;
        const stop = (event) => event.stopPropagation();
        return (
            <span className="job-quick" onClick={stop}>
                <button type="button" className="jq-trigger" aria-label={t('home.actionsFor', { name: job.name })} aria-expanded={menuOpen}
                    onClick={() => setQuickMenuId(menuOpen ? '' : job.id)}><IconMore /></button>
                {menuOpen && <span className="jq-menu">
                    <button type="button" onClick={() => openJob(job)}><IconReports />{t('home.viewJob')}</button>
                    <a className={!current.phone ? 'disabled' : ''} href={current.phone ? `tel:${current.phone}` : undefined}><IconPhone />{t('home.callClient')}</a>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stripDiacritics(current.address || ''))}`} target="_blank" rel="noreferrer"><IconPin />{t('home.openInMaps')}</a>
                    <button type="button" onClick={() => openQuickPanel('status', current)}><IconPulse />{t('home.changeStatus')}</button>
                    {!current.assigned && <button type="button" onClick={() => openJob(current)}><IconUser />{t('home.assignEmployee')}</button>}
                    {current.invoiceAvailable && <button type="button" onClick={() => openQuickPanel('invoice', current)}><IconMail />{t('home.sendInvoice')}</button>}
                </span>}
            </span>
        );
    };

    const QuickActionPanel = () => {
        if (!quickPanel) return null;
        const { type, job } = quickPanel;
        if (type === 'invoice') return <InvoiceSharePanel invoice={{ jobId: job.id, clientName: job.name, email: job.email, phone: job.phone }} onClose={() => setQuickPanel(null)} />;
        return (
            <div className="qap-backdrop" onMouseDown={() => setQuickPanel(null)}>
                <aside className="qap" onMouseDown={(event) => event.stopPropagation()} aria-label={t('home.quickActionPanel')}>
                    <div className="qap-head"><div><b>{type === 'status' ? t('home.changeStatus') : t('home.sendInvoice')}</b><span>{t('jobs.jobNumber', { id: job.id })} · {job.name}</span></div><button type="button" onClick={() => setQuickPanel(null)}>×</button></div>
                    {type === 'status' && <div className="qap-form status">
                        <div className="qap-status-list">
                            {getStatusOptions().map(({ value, label }) => <button type="button" key={value} className={quickStatus === value ? 'selected' : ''} onClick={() => setQuickStatus(value)}>
                                <span className={`qap-status-dot ${stage(value).cls}`} />
                                <span>{label}</span>
                                {quickStatus === value && <IconCheck />}
                            </button>)}
                        </div>
                        {statusError && <div className="qap-share-note error">{statusError}</div>}
                        <button type="button" className="qap-save" disabled={savingStatus || quickStatus === job.status} onClick={() => saveQuickStatus(job)}>{savingStatus ? t('common.saving') : quickStatus === job.status ? t('home.currentStatus') : t('home.updateStatus')}</button>
                    </div>}
                </aside>
            </div>
        );
    };

    const DailyBrief = ({ desktop = false }) => (
        <section className={`daily-brief${desktop ? ' daily-brief-d' : ''}`} aria-labelledby={`daily-brief-${desktop ? 'desktop' : 'mobile'}`}>
            <div className="db-head">
                <div>
                    <h1 id={`daily-brief-${desktop ? 'desktop' : 'mobile'}`}>{t('home.overview', { company: companyName })}</h1>
                    <p>{toLocalDateString(now, 'EEEE, d MMMM yyyy')} <span>•</span> {t('home.week', { number: isoWeek(now) })}</p>
                </div>
                <div className="db-actions">
                    {/* Office roles only. Drivers and technicians can read clients,
                        so a /clients path check let these through; the API refuses
                        all four actions for field roles (permissions.md). */}
                    {officeUser && (
                        <>
                            <button type="button" className="db-action primary" onClick={() => setNewJobModal(true)}><IconPlus />{t('home.newJob')}</button>
                            <button type="button" className="db-action client" onClick={() => setNewClientModal(true)}><IconUser />{t('home.newClient')}</button>
                            <button type="button" className="db-action" onClick={() => setIntakeLinkDrawer(true)} title={t('home.customerRequestLinkHint')}>
                                <IconMail />{t('home.customerRequestLink')}
                            </button>
                            <input ref={scanFileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" onChange={onPickScanFile} hidden />
                            <button type="button" className="db-action scan" onClick={() => scanFileRef.current?.click()} title={t('home.scanHint', { assistant: t('askSoroby.title') })}>
                                <IconCamera />{t('home.scan')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="db-greeting">{t(greetingKey(now.getHours()))}, <em>{firstName}</em> <span aria-hidden="true">👋</span></div>

            <div className="ov-search db-search">
                <JobsSelect onChange={(option) => option?.value && navigate('/jobs?jobId=' + option.value)} />
            </div>

            {allowed && unpaidCount > 0 &&
                <button type="button" className="db-alert" onClick={() => navigate('/jobs?paymentStatus=PENDING')}>
                    <span className="db-alert-icon"><IconAlert /></span>
                    <span><b>{t('home.unpaidInvoices', { count: unpaidCount })}</b><small>{t('home.reviewOutstandingPayments')}</small></span>
                    <strong>{t('common.view')} ›</strong>
                </button>
            }

            <div className="db-section-title"><span>{t('home.todayAtAGlance')}</span><small><i /> {t('home.updatedJustNow')}</small></div>
            <div className="db-stats">
                <div className="db-stat blue clickable" role="button" tabIndex={0} onClick={scrollToTodaysJobs} onKeyDown={(event) => event.key === 'Enter' && scrollToTodaysJobs()}>
                    <IconCalendar /><b>{loadingToday ? '…' : fmt(todayJobs.length)}</b><span>{t('home.jobsToday')}</span>
                </div>
                <div className="db-stat purple clickable" role="button" tabIndex={0} onClick={scrollToPickups} onKeyDown={(event) => event.key === 'Enter' && scrollToPickups()}>
                    <IconPickup /><b>{loadingToday ? '…' : fmt(pickupCount)}</b><span>{t('home.pickups')}</span>
                </div>
                <div className="db-stat green clickable" role="button" tabIndex={0} onClick={scrollToDeliveries} onKeyDown={(event) => event.key === 'Enter' && scrollToDeliveries()}>
                    <IconTruck /><b>{loadingToday ? '…' : fmt(deliveryCount)}</b><span>{t('home.deliveries')}</span>
                </div>
                {allowed &&
                    <div className="db-stat orange clickable" role="button" tabIndex={0} onClick={() => navigate('/jobs?paymentStatus=PENDING')} onKeyDown={(event) => event.key === 'Enter' && navigate('/jobs?paymentStatus=PENDING')}>
                        <IconWallet /><b>{loadingTotals ? '…' : rsdFormat(unpaidAmt, false)}</b><span>{t('home.toCollect', { currency: 'RSD' })}</span>
                    </div>
                }
                <div className="db-stat red clickable" role="button" tabIndex={0} onClick={scrollToNeedsAttention} onKeyDown={(event) => event.key === 'Enter' && scrollToNeedsAttention()}>
                    <IconAlert /><b>{loadingToday ? '…' : fmt(dashboard.dailyBrief?.needsAttention)}</b><span>{t('home.needAttention')}</span>
                </div>
            </div>

            <div className="db-insight">
                <div className="db-insight-title"><IconSparkle /> {t('home.dailyInsight')}</div>
                <ul>
                    <li>{unassignedCount === 0 ? t('home.allJobsAssigned') : t('home.jobsUnassignedCount', { count: unassignedCount })}</li>
                    <li>{unpaidCount === 0 ? t('home.allInvoicesSettled') : t('home.invoicesAwaitingPaymentCount', { count: unpaidCount })}</li>
                    <li>{todayJobs.length > 0 ? t('home.firstScheduledJob', { time: hm(todayJobs[0]?.scheduleTime) }) : t('home.noJobsScheduled')}</li>
                </ul>
            </div>
        </section>
    );

    const attentionItems = [
        ...(lowStockCount > 0 ? [{ key: 'lowStock', count: lowStockCount, title: t('home.lowStockMaterials'), subtitle: t('home.atOrBelowMinimumStock'), icon: IconAlert, tone: 'orange', action: () => navigate('/materials') }] : []),
        { key: 'unassigned', count: unassignedCount, title: t('home.unassignedJobs'), subtitle: t('home.assignStaffToJobs'), icon: IconUser, tone: 'orange', action: () => navigate('/jobs?attention=unassigned') },
        { key: 'overdue', count: overduePickupCount, title: t('home.overduePickup'), subtitle: t('home.pickupWasScheduledEarlier'), icon: IconPickup, tone: 'red', action: () => navigate('/jobs?attention=overdue') },
        { key: 'waiting', count: waiting24hCount, title: t('home.jobsWaiting24h'), subtitle: t('home.noStatusChange'), icon: IconClock, tone: 'orange', action: () => navigate('/jobs?attention=waiting') },
        { key: 'reschedule', count: needsRescheduleCount, title: MISC_LABELS.needsRescheduling, subtitle: t('home.customerUnavailableReschedule'), icon: IconCalendar, tone: 'red', action: () => navigate('/jobs?attention=reschedule') },
        { key: 'unpaid', count: unpaidCount, title: t('home.unpaidInvoicesTitle'), subtitle: t('home.outstandingPayments'), icon: IconAlert, tone: 'red', action: () => navigate('/jobs?paymentStatus=PENDING') },
        { key: 'overdueInvoices', count: overdueInvoicesCount, title: t('home.overdueInvoices'), subtitle: t('home.pastPaymentDueDate'), icon: IconAlert, tone: 'red', action: () => navigate('/jobs?paymentStatus=PENDING') },
        { key: 'notes', count: withoutNotesCount, title: t('home.jobsWithoutNotes'), subtitle: t('home.addNotesToJobs'), icon: IconNote, tone: 'green', action: () => navigate('/jobs?attention=notes') },
    ];

    const NeedsAttention = ({ idPrefix = 'm' }) => (
        <section id={`${idPrefix}-section-attention`} className="needs-attention" aria-labelledby="needs-attention-title">
            <div className="na-heading">
                <h2 id="needs-attention-title">{t('home.needsYourAttention')}</h2>
                <p>{t('home.theseItemsNeedAction')}</p>
            </div>
            <div className="na-list">
                {attentionItems.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                        <button type="button" className={`na-row ${item.tone}`} key={item.key} onClick={item.action}>
                            <span className="na-icon"><ItemIcon /></span>
                            <span className="na-count num">{item.count}</span>
                            <span className="na-copy"><b>{item.title}</b><small>{item.subtitle}</small></span>
                            <IconChevron className="na-chevron" />
                        </button>
                    );
                })}
            </div>
            <button type="button" className="na-view-all" onClick={() => navigate('/jobs')}>{t('home.viewAllIssues')}</button>
        </section>
    );

    const scheduledJobsForDisplay = todayJobs;
    const pickupsForDisplay = upcomingPickups;
    const deliveriesForDisplay = upcomingDeliveries;

    const TodaysJobs = ({ idPrefix = 'm' }) => (
        <section id={`${idPrefix}-section-todays-jobs`} className="today-schedule" aria-labelledby="today-schedule-title">
            <div className="ts-heading">
                <h2 id="today-schedule-title">{t('home.todaysJobs')}</h2>
                <p>{t('home.yourScheduleForToday')}</p>
            </div>
            {loadingToday
                ? <div className="ts-empty">{t('home.loadingTodaysSchedule')}</div>
                : scheduledJobsForDisplay.length === 0
                    ? <div className="ts-empty"><IconCalendar /><span>{t('home.nothingScheduledForToday')}</span></div>
                    : <div className="ts-timeline">
                        {scheduledJobsForDisplay.map((job, index) => {
                            const jobStage = stage(job.status);
                            const area = m2(job.area);
                            return (
                                <div className={`ts-job ${jobStage.cls}`} role="button" tabIndex="0" key={job.id || index} onClick={() => openJob(job)} onKeyDown={(event) => event.key === 'Enter' && openJob(job)}>
                                    <span className="ts-time num">{hm(job.scheduleTime)}</span>
                                    <span className="ts-track"><i /></span>
                                    <span className="ts-details">
                                        <span className={`badge-sbr ${jobStage.badge}`}>{jobStage.label}</span>
                                        <b>{job.name}</b>
                                        <small>{job.address || t('common.addressNotProvided')}</small>
                                    </span>
                                    {area && <span className="ts-area">{area}</span>}
                                    <QuickActions job={job} />
                                    <IconChevron className="ts-chevron" />
                                </div>
                            );
                        })}
                    </div>
            }
            <button type="button" className="ts-view-all" onClick={() => navigate('/jobs')}>{t('home.viewFullSchedule')}</button>
        </section>
    );

    const UpcomingPickups = ({ idPrefix = 'm' }) => (
        <section id={`${idPrefix}-section-pickups`} className="upcoming-pickups" aria-labelledby="upcoming-pickups-title">
            <div className="up-heading">
                <h2 id="upcoming-pickups-title">{t('home.scheduledJobs')}</h2>
                <p>{t('home.nextScheduledJobs')}</p>
            </div>
            {loadingPickups
                ? <div className="up-empty">{t('home.loadingScheduledJobs')}</div>
                : pickupsForDisplay.length === 0
                    ? <div className="up-empty"><IconTruck /><span>{t('home.noScheduledJobs')}</span></div>
                    : <div className="up-list">
                        {pickupsForDisplay.slice(0, 5).map((job, index) => (
                            <div className="up-row" role="button" tabIndex="0" key={job.id || index} onClick={() => openJob(job)} onKeyDown={(event) => event.key === 'Enter' && openJob(job)}>
                                <span className="up-time num">{hm(job.pickupTime)}</span>
                                <span className="up-icon"><IconTruck /></span>
                                <span className="up-copy">
                                    <b>{job.name}</b>
                                    <small>{job.address || t('common.addressNotProvided')}</small>
                                </span>
                                <QuickActions job={job} />
                                <IconChevron className="up-chevron" />
                            </div>
                        ))}
                    </div>
            }
            <button type="button" className="up-view-all" onClick={() => navigate('/jobs')}>{t('home.viewAllScheduledJobs')}</button>
        </section>
    );

    const UpcomingDeliveries = ({ idPrefix = 'm' }) => (
        <section id={`${idPrefix}-section-deliveries`} className="upcoming-pickups upcoming-deliveries" aria-labelledby="upcoming-deliveries-title">
            <div className="up-heading">
                <h2 id="upcoming-deliveries-title">{t('home.readyJobs')}</h2>
                <p>{t('home.jobsReadyForDelivery')}</p>
            </div>
            {loadingDeliveries
                ? <div className="up-empty">{t('home.loadingReadyJobs')}</div>
                : deliveriesForDisplay.length === 0
                    ? <div className="up-empty"><IconTruck /><span>{t('home.noReadyJobs')}</span></div>
                    : <div className="up-list">
                        {deliveriesForDisplay.slice(0, 5).map((job, index) => (
                            <div className="up-row" role="button" tabIndex="0" key={job.id || index} onClick={() => openJob(job)} onKeyDown={(event) => event.key === 'Enter' && openJob(job)}>
                                <span className="up-time num">{hm(job.deliveryTime)}</span>
                                <span className="up-icon"><IconTruck /></span>
                                <span className="up-copy">
                                    <b>{job.name}</b>
                                    <small>{job.address || t('common.addressNotProvided')}</small>
                                </span>
                                <QuickActions job={job} />
                                <IconChevron className="up-chevron" />
                            </div>
                        ))}
                    </div>
            }
            <button type="button" className="up-view-all" onClick={() => navigate('/jobs')}>{t('home.viewAllReadyJobs')}</button>
        </section>
    );

    const FinishedToday = () => {
        const completedCount = finishedToday.length;
        const paidFinished = finishedToday.filter((job) => job.payment === 'PAID').length;
        const pendingFinished = finishedToday.filter((job) => job.payment !== 'PAID').length;
        const yesterdayCount = finishedYesterday.length;
        const change = yesterdayCount > 0 ? Math.round(((completedCount - yesterdayCount) / yesterdayCount) * 100) : null;
        const paidWidth = completedCount > 0 ? (paidFinished / completedCount) * 100 : 0;
        const pendingWidth = completedCount > 0 ? (pendingFinished / completedCount) * 100 : 0;

        const goToCompleted = () => navigate('/reports/jobs-history');
        return (
            <section className="finished-today clickable" aria-labelledby="finished-today-title"
                role="button" tabIndex={0} onClick={goToCompleted} onKeyDown={(event) => event.key === 'Enter' && goToCompleted()}>
                <div className="ft-heading">
                    <h2 id="finished-today-title">{t('home.completedToday')}</h2>
                    <p>{t('home.jobsCompletedToday')}</p>
                </div>
                <div className="ft-summary">
                    <div><b className="num">{completedCount}</b><span>{t('home.jobsCompleted')}</span></div>
                    <div className="ft-trend">
                        {change == null
                            ? <strong className="flat">—</strong>
                            : <strong className={change >= 0 ? 'up' : 'down'}>{change >= 0 ? '↑' : '↓'} {Math.abs(change)}%</strong>}
                        <span>{t('home.vsYesterday')}</span>
                    </div>
                </div>
                <div className="ft-breakdown">
                    <div className="ft-line paid">
                        <div className="ft-label"><span><i />{t('common.paid')}</span><b>{t('home.job', { count: paidFinished })}</b></div>
                        <div className="ft-bar"><i style={{ width: `${paidWidth}%` }} /></div>
                    </div>
                    <div className="ft-line pending">
                        <div className="ft-label"><span><i />{t('home.pendingPayment')}</span><b>{t('home.job', { count: pendingFinished })}</b></div>
                        <div className="ft-bar"><i style={{ width: `${pendingWidth}%` }} /></div>
                    </div>
                </div>
                <button type="button" className="ft-view-all" onClick={(event) => { event.stopPropagation(); goToCompleted(); }}>{t('home.viewAllCompletedJobs')}</button>
            </section>
        );
    };

    const PaymentOverview = ({ idPrefix = 'm' }) => {
        if (!allowed) return null;
        const goToInvoices = () => navigate('/sales');
        return (
            <section className="payment-overview clickable" aria-labelledby="payment-overview-title"
                role="button" tabIndex={0} onClick={goToInvoices} onKeyDown={(event) => event.key === 'Enter' && goToInvoices()}>
                <div className="po-heading">
                    <h2 id="payment-overview-title">{t('home.paymentSplit')}</h2>
                    <p>{t('home.paidVsUnpaid')}</p>
                </div>
                <div className="po-content">
                    <div className="po-donut">
                        <Donut paidColor="#10A96B" unpaidColor="#EF202B" />
                        <div className="po-donut-label"><b className="num">{paidPct}%</b><span>{t('common.paid')}</span></div>
                    </div>
                    <div className="po-legend">
                        <div className="po-line paid">
                            <i />
                            <div className="po-name"><b>{t('common.paid')}</b></div>
                            <div className="po-value"><b>{rsdFormat(paidAmt)}</b><span>{fmt(paidCount)} {t('home.invoices')} <em>•</em> {paidPct}%</span></div>
                        </div>
                        <div className="po-line unpaid">
                            <i />
                            <div className="po-name"><b>{t('common.unpaid')}</b></div>
                            <div className="po-value"><b>{rsdFormat(unpaidAmt)}</b><span>{fmt(unpaidCount)} {t('home.invoices')} <em>•</em> {unpaidPct}%</span></div>
                        </div>
                    </div>
                </div>
                <button type="button" className="po-view-all" onClick={(event) => { event.stopPropagation(); goToInvoices(); }}>{t('common.viewAll')} {t('home.invoices')}</button>
            </section>
        );
    };

    const RevenueOverview = () => {
        if (!allowed) return null;
        return (
            <section className="revenue-overview" aria-label={t('home.revenueLast6Months')}>
                <button type="button" className="ro-report" onClick={() => navigate('/reports/jobs-history')}>{t('reports.viewReport')}</button>
                <RevenueAreaChart />
            </section>
        );
    };

    return (
        <div className="sbr sb-dash">
            <SetTempEditOrderModal display={editJobModal} toggle={setEditJobModal} jobId={selectedJobId} />
            <SetOrderModal display={newJobModal} toggle={setNewJobModal} />
            <SetClientModal display={newClientModal} toggle={setNewClientModal} asDrawer />
            <IntakeLinkDrawer show={intakeLinkDrawer} onHide={() => setIntakeLinkDrawer(false)} />
            <QuickActionPanel />

            {/* ============================ MOBILE ============================ */}
            <div className="sbr-m">
                <DailyBrief />
                <NeedsAttention idPrefix="m" />
                <TodaysJobs idPrefix="m" />
                <UpcomingPickups idPrefix="m" />
                <UpcomingDeliveries idPrefix="m" />
                <FinishedToday />
                <PaymentOverview idPrefix="m" />
                <RevenueOverview />
                <div className="hello">
                    <div className="hi">{t(greetingKey(now.getHours()))}, <em>{firstName}</em></div>
                    <div className="dt">
                        <span>{toLocalDateString(now, 'EEEE, d MMMM yyyy')}</span>
                        <span className="dot" /><span>{t('home.week', { number: isoWeek(now) })}</span>
                    </div>
                </div>

                <div className="ov-search">
                    <JobsSelect onChange={(option) => option?.value && navigate('/jobs?jobId=' + option.value)} />
                </div>

                {allowed && unpaidCount > 0 &&
                    <div className="attn" onClick={() => navigate('/jobs?paymentStatus=PENDING')}>
                        <div className="ico"><IconAlert className="ic" /></div>
                        <div className="tx"><b>{t('home.unpaidInvoices', { count: unpaidCount })}</b><span>{t('home.reviewOutstandingPayments')}</span></div>
                        <div className="go">{t('common.view')} ›</div>
                    </div>
                }

                <div className="kpis">
                    <div className="kpi b1">
                        <div className="ico"><IconPulse className="ic" /></div>
                        <div className="l">{t('home.jobsThisWeek')}</div>
                        <div className="n num">{loadingTotals ? '…' : fmt(twTotal)}</div>
                        <Mdelta tr={totals?.trend?.total} />
                    </div>
                    <div className="kpi b2">
                        <div className="ico"><IconCheck className="ic" /></div>
                        <div className="l">{t('home.completed')}</div>
                        <div className="n num">{loadingTotals ? '…' : fmt(twCompleted)}</div>
                        <Mdelta tr={totals?.trend?.completed} />
                    </div>
                    {allowed && <>
                        <div className="kpi b3">
                            <div className="ico"><IconCard className="ic" /></div>
                            <div className="l">{t('home.paidThisWeek')}</div>
                            <div className="n cur num">{loadingTotals ? '…' : <>{rsdFormat(twPaid, false)} <span className="cur-sfx">RSD</span></>}</div>
                            <Mdelta tr={totals?.trend?.paid} />
                        </div>
                        <div className="kpi b4">
                            <div className="ico"><IconClock className="ic" /></div>
                            <div className="l">{t('home.unpaid')}</div>
                            <div className="n cur num">{loadingTotals ? '…' : <>{rsdFormat(unpaidAmt, false)} <span className="cur-sfx">RSD</span></>}</div>
                            <div className="d down">{t('home.invoice', { count: unpaidCount })}{unpaidCount === 0 ? ` · ${t('home.allSettled')}` : ''}</div>
                        </div>
                    </>}
                </div>

                {allowed &&
                    <div className="card-sbr legacy-payment-card">
                        <div className="ch"><IconWallet className="ci ic" /><span className="t">{t('home.paymentSplit')}</span><span className="more">{t('home.paidVsUnpaid')}</span></div>
                        <div className="split">
                            <div className="donut">
                                <Donut paidColor="var(--done)" />
                                <div className="ctr"><span className="pct num">{paidPct}%</span><span className="cap">{t('common.paid')}</span></div>
                            </div>
                            <div className="legend">
                                <div className="leg"><span className="sw paid" /><span className="lt"><b>{t('common.paid')}</b><span>{fmt(paidCount)} {t('home.invoices')} · {paidPct}%</span></span><span className="lv num">{rsdFormat(paidAmt, false)}</span></div>
                                <div className="leg"><span className="sw unpaid" /><span className="lt"><b>{t('common.unpaid')}</b><span>{fmt(unpaidCount)} {t('home.invoices')} · {unpaidPct}%</span></span><span className="lv num">{rsdFormat(unpaidAmt, false)}</span></div>
                                <div className="leg" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 11 }}>
                                    <span className="lt"><b style={{ color: 'var(--muted)', fontWeight: 700 }}>{t('home.billedTotal')}</b></span>
                                    <span className="lv num">{rsdFormat(billedAmt)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                }

                {allowed &&
                    <div className="card-sbr legacy-revenue-card">
                        <RevenueAreaChart />
                    </div>
                }

                <div className="card-sbr legacy-today-card" style={{ marginBottom: 4 }}>
                    <div className="ch"><IconCalendar className="ci ic" /><span className="t">{t('home.todaysJobs')}</span>
                        <span className="more" onClick={() => navigate('/jobs')}>{t('common.all')} {todayJobs.length} ›</span>
                    </div>
                    <div className="legacy-today-empty">{todaysJobRows}</div>
                    {todayJobs.map((j, i) => {
                        const s = stage(j.status); const area = m2(j.area);
                        return (
                            <div className={`tj ${s.cls}`} key={`m-tj-${i}`} onClick={() => openJob(j)} style={{ cursor: 'pointer' }}>
                                <div className="tm"><div className="hm num">{hm(j.pickupTime)}</div></div>
                                <div className="bar" />
                                <div className="info">
                                    <div className="nm">{j.name}</div>
                                    <div className="meta">
                                        <span className={`badge-sbr ${s.badge}`}>{s.label}</span>
                                        <span className="muted" style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{area || j.address}</span>
                                    </div>
                                </div>
                                <div className="rt">
                                    {j.price != null && <div className="v num">{rsdFormat(Number(j.price), false)}</div>}
                                    {j.assigned && <div className="vs" style={{ color: 'var(--muted)' }} title={j.assigned}>{getInitials(j.assigned)}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ============================ DESKTOP ============================ */}
            <div className="sbr-d">
                <DailyBrief desktop />
                <div className="overview-card-grid">
                    <NeedsAttention idPrefix="d" />
                    <TodaysJobs idPrefix="d" />
                    <UpcomingPickups idPrefix="d" />
                    <UpcomingDeliveries idPrefix="d" />
                    <FinishedToday />
                    <PaymentOverview idPrefix="d" />
                    <RevenueOverview />
                </div>
                <div className="dhead">
                    <div>
                        <div className="ttl">{t('nav.overview')}</div>
                        <div className="sub">{toLocalDateString(now, 'EEEE, d MMMM')} · {t(greetingKey(now.getHours()))}, {firstName}</div>
                    </div>
                    <div className="act">
                        <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => setNewJobModal(true)}><IconPulse style={{ width: 15, height: 15 }} />{t('home.newJob')}</button>
                        <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => navigate('/reports/jobs-history')}><IconReports style={{ width: 15, height: 15 }} />{t('nav.reports')}</button>
                    </div>
                </div>

                <div className="ov-search">
                    <JobsSelect onChange={(option) => option?.value && navigate('/jobs?jobId=' + option.value)} />
                </div>

                <div className="kpis4">
                    <div className="kpi">
                        <div className="top"><div className="ig"><IconPulse className="ic" /></div><Ddelta tr={totals?.trend?.total} /></div>
                        <div className="l">{t('home.jobsThisWeek')}</div>
                        <div className="n num">{loadingTotals ? '…' : fmt(twTotal)}</div>
                        <div className="sub2">{lwTotal > 0 ? `${fmt(lwTotal)} ${t('home.vsLastWeek')}` : t('home.thisISOWeek')}</div>
                    </div>
                    <div className="kpi">
                        <div className="top"><div className="ig"><IconCheck className="ic" /></div><Ddelta tr={totals?.trend?.completed} /></div>
                        <div className="l">{t('home.completed')}</div>
                        <div className="n num">{loadingTotals ? '…' : fmt(twCompleted)}</div>
                        <div className="sub2">{twTotal > 0 ? t('home.ofTheWeeksJobs', { pct: weekCompletedPct }) : ' '}</div>
                    </div>
                    {allowed &&
                        <div className="kpi lift">
                            <div className="top"><div className="ig"><IconCard className="ic" /></div><Ddelta tr={totals?.trend?.paid} /></div>
                            <div className="l">{t('home.paidThisWeek')}</div>
                            <div className="n num">{loadingTotals ? '…' : rsdFormat(twPaid)}</div>
                            <div className="sub2">{t('home.settledAcrossInvoices', { count: fmt(paidCount) })}</div>
                        </div>
                    }
                    {allowed &&
                        <div className="kpi">
                            <div className="top">
                                <div className="ig" style={{ background: 'var(--warn-bg)', borderColor: 'var(--warn-bd)', color: 'var(--warn)' }}><IconClock className="ic" /></div>
                                <span className="delta warn">{t('home.invoice', { count: unpaidCount })}</span>
                            </div>
                            <div className="l">{t('home.unpaid')}</div>
                            <div className="n num" style={{ color: 'var(--warn)' }}>{loadingTotals ? '…' : rsdFormat(unpaidAmt)}</div>
                            <div className="sub2">{unpaidCount > 0 ? t('home.awaitingPayment') : t('home.allSettled')}</div>
                        </div>
                    }
                </div>

                <div className="cols">
                    {/* MAIN COLUMN */}
                    <div>
                        {allowed &&
                            <div className="card-sbr legacy-revenue-card" style={{ margin: 0 }}>
                                <RevenueAreaChart />
                            </div>
                        }

                        <div className="sect legacy-today-section">
                            <span className="t">{t('home.todaysJobs')}</span><span className="dot" />
                            <span className="meta">{toLocalDateString(now, 'EEEE, d MMMM')}</span>
                            <span className="count">{todayJobs.length} {t('customerStages.SCHEDULED').toLowerCase()}</span>
                        </div>

                        <div className="legacy-today-empty">{todaysJobRows}</div>
                        {todayJobs.map((j, i) => {
                            const s = stage(j.status); const area = m2(j.area);
                            return (
                                <div className={`djob legacy-today-job ${s.cls}`} key={`d-djob-${i}`} onClick={() => openJob(j)} style={{ cursor: 'pointer' }}>
                                    <div className="time"><div className="hm num">{hm(j.pickupTime)}</div></div>
                                    <div className="cust">{j.name}</div>
                                    <div className="loc"><IconPin className="ic" style={{ width: 13, height: 13 }} />{j.address || '—'}{area ? ` · ${area}` : ''}</div>
                                    <span className={`badge-sbr ${s.badge}`}>{s.label}</span>
                                    <div className="who">{j.assigned ? <><span className="a">{getInitials(j.assigned)}</span><span className="nm">{j.assigned}</span></> : <span className="nm">{t('jobs.unassigned')}</span>}</div>
                                    {j.price != null && <div className="price">{rsdFormat(Number(j.price), false)}</div>}
                                </div>
                            );
                        })}
                    </div>

                    {/* RIGHT RAIL */}
                    <div className="col-stack" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {allowed && unpaidCount > 0 &&
                            <div className="attn" style={{ margin: 0 }} onClick={() => navigate('/jobs?paymentStatus=PENDING')}>
                                <div className="ico"><IconAlert className="ic" /></div>
                                <div className="tx"><b>{t('home.unpaidInvoices', { count: unpaidCount })}</b><span>{t('home.outstandingPayments')}</span></div>
                                <div className="go">{t('common.view')} ›</div>
                            </div>
                        }

                        {allowed &&
                            <div className="card-sbr legacy-payment-card" style={{ margin: 0 }}>
                                <div className="ch"><IconWallet className="ci ic" /><span className="t">{t('home.paymentSplit')}</span><span className="more">{t('home.invoices')}</span></div>
                                <div className="donut-d">
                                    <div className="ctr">
                                        <Donut paidColor="var(--brand)" />
                                        <div className="cn"><div className="p">{paidPct}%</div><div className="l">{t('common.paid')}</div></div>
                                    </div>
                                    <div className="splitlist">
                                        <div className="sl"><span className="k" style={{ background: 'var(--brand)' }} /><span className="nm">{t('common.paid')}<small>{fmt(paidCount)} {t('home.invoices')}</small></span><span className="vv">{rsdFormat(paidAmt, false)}</span></div>
                                        <div className="sl"><span className="k" style={{ background: 'var(--warn)' }} /><span className="nm">{t('common.unpaid')}<small>{fmt(unpaidCount)} {t('home.invoices')}</small></span><span className="vv">{rsdFormat(unpaidAmt, false)}</span></div>
                                        <div className="sl" style={{ paddingTop: 11, borderTop: '1px solid var(--line-soft)' }}>
                                            <span className="nm" style={{ fontWeight: 800, color: 'var(--ink)' }}>{t('home.billedTotal')}</span>
                                            <span className="vv">{rsdFormat(billedAmt)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }

                        <div className="card-sbr legacy-finished-card" style={{ margin: 0 }}>
                            <div className="ch"><IconCheck className="ci ic" /><span className="t">{t('home.completedToday')}</span>
                                <span className="more" onClick={() => navigate('/reports/jobs-history')}>{t('jobs.history')} ›</span>
                            </div>
                            {finishedToday.length === 0
                                ? <div className="sbr-empty"><IconCheck className="ic" /><span>{t('home.jobsCompletedToday')}</span></div>
                                : finishedToday.slice(0, 6).map((f, i) => (
                                    <div className="row-sbr" key={`fin-${i}`} onClick={() => openJob(f)} style={{ cursor: 'pointer' }}>
                                        <span className="a g">{getInitials(f.name || '')}</span>
                                        <div className="info"><div className="nm">{f.name}</div><div className="sub">{f.completedTime ? toLocalDateString(f.completedTime, 'HH:mm') : ''} · {f.address}</div></div>
                                        <div className="rt"><div className="v">{f.totalPrice}</div>
                                            <span className={`pill ${f.payment === 'PAID' ? 'paid' : 'due'}`}>{f.payment === 'PAID' ? t('common.paid') : t('common.unpaid')}</span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* mobile KPI delta line */
const Mdelta = ({ tr }) => {
    const { t } = useTranslation();
    const ti = trendInfo(tr);
    const prefix = ti.cls === 'up' ? '▲ ' : ti.cls === 'down' ? '▼ ' : '';
    const label = ti.labelKey ? t(ti.labelKey) : ti.label;
    const sub = ti.subKey ? t(ti.subKey) : undefined;
    return <div className={`d ${ti.cls}`}>{prefix}{label}{sub ? ` ${sub}` : ''}</div>;
};

/* desktop KPI delta chip */
const Ddelta = ({ tr }) => {
    const { t } = useTranslation();
    const ti = trendInfo(tr);
    const A = ti.arrow ? IconArrowUR : IconArrowDR;
    const label = ti.labelKey ? t(ti.labelKey) : ti.label;
    return <span className={`delta ${ti.cls}`}>{ti.cls !== 'flat' && <A className="ic" />}{label}</span>;
};

export default HomePage;
